import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LoopIcon from '@material-ui/icons/Loop';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { useListing, useLanguage, useAuth } from 'hooks';
import { showModal, hideModal } from 'state/actions';
import { MODAL, ENG, COLOR, VIDEO_STATUS, FR, SUBSCRIPTION } from 'config/constants';
import { HvModal, HvButton } from 'components';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import { SlideshowModalStepper } from '../components';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    '@media (min-width: 767px)': {
      minWidth: 400,
    },
  },
  warningContent: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    '@media (min-width: 767px)': {
      minWidth: 400,
    },
    alignItems: 'center',
    minHeight: 200,
  },
  items: {
    overflowY: 'scroll',
    maxHeight: 500,
  },
  videoContent: {
    backgroundColor: COLOR.WHITE,
    padding: 10,
    color: COLOR.BLACK,
    border: `1px solid ${COLOR.GREY}`,
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
  },
  theme_name: {
    fontWeight: 'bold',
  },
  newOrderButton: {
    marginTop: 20,
  },
  addIcon: {
    marginRight: 5,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 10,
  },
  description: {
    color: COLOR.LIGHT_BLUE,
  },
  info: {
    color: COLOR.DARK_GREY,
    fontSize: 13,
  },
  icon: {
    color: COLOR.LIGHT_BLUE,
  },
  successIcon: {
    color: COLOR.GREEN,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
  },
  link: {
    display: 'inline',
    color: COLOR.LIGHT_BLUE,
    textDecoration: 'underline',
  },
});

function SlideshowModal() {
  const intl = useIntl();
  const styles = useStyles();
  const dispatch = useDispatch();
  const language = useLanguage();
  const { user } = useAuth();
  const path = window.location.pathname;
  const arr = path.split('/');
  const listing_token = arr[arr.length - 1];
  const { listing } = useListing(listing_token);

  const theme_lang = language === ENG ? 'en' : FR;

  const [showModalStepper, setShowModalStepper] = useState(!listing.automated_videos.length);

  const fmtWaiting = intl.formatMessage({ id: 'deliver.video_slideshow.waiting' });
  const fmtRequestedVideos = intl.formatMessage({ id: 'deliver.video_slideshow.requestedVideos' });
  const fmtOrderSlideShow = intl.formatMessage({ id: 'deliver.video_slideshow.order' });
  const fmtSlideshow = intl.formatMessage({ id: 'deliver.video_slideshow.title' });
  const fmtNoOrderedSlideshow = intl.formatMessage({
    id: 'deliver.video_slideshow.noOrderedSlideshow',
  });
  const fmtPleaseGoTo = intl.formatMessage({ id: 'deliver.video_slideshow.pleaseGoTo' });
  const fmtProfilePage = intl.formatMessage({ id: 'deliver.video_slideshow.profilePage' });
  const fmtChangeSubscription = intl.formatMessage({
    id: 'deliver.video_slideshow.changeSubscription',
  });
  const fmtHaveThisFeature = intl.formatMessage({
    id: 'deliver.video_slideshow.haveThisFeature',
  });
  const fmtTransferCentrisData = intl.formatMessage({
    id: 'deliver.video_slideshow.transferCentrisData',
  });
  const onClose = () => {
    dispatch(hideModal(MODAL.SLIDESHOW));
  };

  const onCreateSlideshowOrder = async () => {
    setShowModalStepper(true);
  };
  const renderOrderInfo = (
    <Box className={styles.content}>
      <Box className={styles.items}>
        {listing.automated_videos.length !== 0 && (
          <Typography className={styles.title}>{fmtRequestedVideos}</Typography>
        )}
        {listing.automated_videos.map((video, index) => {
          return (
            <Box key={index} className={styles.videoContent}>
              <Box>
                <Typography className={styles.theme_name}>
                  {`${video.video_template[`theme_${theme_lang}`]}`}
                </Typography>
                <Typography className={styles.info}>
                  {[VIDEO_STATUS.requested, VIDEO_STATUS.enqueued].includes(video.status)
                    ? fmtWaiting
                    : null}
                </Typography>
              </Box>
              {[VIDEO_STATUS.requested, VIDEO_STATUS.enqueued].includes(video.status) ? (
                <LoopIcon className={styles.icon} />
              ) : null}
              {video.status === VIDEO_STATUS.success ? (
                <CheckRoundedIcon className={styles.successIcon} />
              ) : null}
            </Box>
          );
        })}
        {!listing.automated_videos.length && (
          <Typography className={styles.description}>{fmtNoOrderedSlideshow}</Typography>
        )}
      </Box>
      <HvButton className={styles.newOrderButton} onClick={onCreateSlideshowOrder}>
        <AddCircleIcon className={styles.addIcon} />
        {fmtOrderSlideShow}
      </HvButton>
    </Box>
  );
  const modalContent = () => {
    if (showModalStepper) {
      return <SlideshowModalStepper onClose={onClose} />;
    }
    return renderOrderInfo;
  };
  const changeSubscription = () => {
    if (listing.broker.completedCentrisDataTransfer) {
      return (
        <Box className={styles.warningContent}>
          <Typography>
            <span>{fmtPleaseGoTo}</span>
            <span>
              <a
                onClick={() => {
                  dispatch(hideModal(MODAL.SLIDESHOW));
                  window.open('/profile', '_blank');
                }}
              >
                <Typography className={styles.link}>{fmtProfilePage}</Typography>
              </a>
            </span>
            <span>{fmtChangeSubscription}</span>
          </Typography>
        </Box>
      );
    }
    return (
      <Box className={styles.warningContent}>
        <Typography>
          <span>{fmtHaveThisFeature}</span>
          <span>
            <a
              onClick={() => {
                dispatch(hideModal(MODAL.SLIDESHOW));
                window.open('/profile', '_blank');
              }}
            >
              <Typography className={styles.link}>{fmtProfilePage}</Typography>
            </a>
          </span>
          <span>{fmtTransferCentrisData}</span>
        </Typography>
      </Box>
    );
  };

  return (
    <HvModal title={fmtSlideshow} headerImage='slideshow' onClose={onClose}>
      {(listing.broker.subscription_id === SUBSCRIPTION.plateforme || user.isAdmin) &&
      !listing.broker.completedCentrisDataTransfer
        ? modalContent()
        : changeSubscription()}
    </HvModal>
  );
}
export default SlideshowModal;
